import { useState, useCallback } from 'react';
import { Search, Loader2 } from 'lucide-react';
import { useDebouncedCallback } from 'use-debounce';

interface DomainSearchInputProps {
  onSearch: (query: string) => void;
  isSearching: boolean;
  initialValue?: string;
}

export function DomainSearchInput({
  onSearch,
  isSearching,
  initialValue = '',
}: DomainSearchInputProps) {
  const [query, setQuery] = useState(initialValue);

  // Debounced search - triggers 400ms after user stops typing
  const debouncedSearch = useDebouncedCallback((value: string) => {
    const cleanValue = value.trim().toLowerCase();
    if (cleanValue.length >= 2) {
      onSearch(cleanValue);
    }
  }, 400);

  const handleChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const value = e.target.value;
      // Remove any TLD suffix and special characters as they type
      const cleanValue = value
        .replace(/\.[a-z]+$/i, '')
        .replace(/[^a-zA-Z0-9-]/g, '');
      setQuery(cleanValue);
      debouncedSearch(cleanValue);
    },
    [debouncedSearch]
  );

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      const cleanQuery = query.trim().toLowerCase();
      if (cleanQuery.length >= 2) {
        debouncedSearch.cancel();
        onSearch(cleanQuery);
      }
    }
  };

  return (
    <div className="relative">
      <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none">
        {isSearching ? (
          <Loader2 className="w-5 h-5 text-gray-400 animate-spin" />
        ) : (
          <Search className="w-5 h-5 text-gray-400" />
        )}
      </div>
      <input
        type="text"
        value={query}
        onChange={handleChange}
        onKeyDown={handleKeyDown}
        placeholder="Enter your business name to find available domains..."
        className="w-full pl-12 pr-4 py-4 text-lg border border-gray-300 rounded-xl focus:ring-2 focus:ring-primary-500 focus:border-primary-500 transition-all"
        autoComplete="off"
        autoCorrect="off"
        autoCapitalize="off"
        spellCheck="false"
      />
      {query.length > 0 && query.length < 2 && (
        <p className="absolute -bottom-5 left-0 text-xs text-gray-400">
          Enter at least 2 characters
        </p>
      )}
    </div>
  );
}
